import React, { useEffect, useMemo, useState } from "react";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import { API_report_email_subscription } from "../../../../constants";
import { getRightworkAPI } from "../../requests/utilities/requests";
import { unwrapPayload } from "./reports/_shared/laborMath";
import {
  emailedReportsVersion,
  getCachedEmailedReports,
  storeEmailedReports,
} from "./reportEmailBadges";
import {
  PREBUILT_REPORTS,
  REPORT_GROUPS,
  STATUS_BADGES,
  getEligibleStoresForReport,
  reportRequiresScheduling,
  reportVisibleForPermission,
} from "./reportRegistry";

function ReportTile({ report, onOpen, emailed }) {
  const isAvailable = report.status === "available";
  const badge = STATUS_BADGES[report.status] || STATUS_BADGES["coming-soon"];

  const baseClasses =
    "text-left rounded-lg border p-4 transition-all flex flex-col h-full min-h-[118px]";
  const interactiveClasses = isAvailable
    ? "bg-white border-slate-200 hover:border-violet-400 hover:shadow-md cursor-pointer"
    : "bg-slate-50 border-slate-200";
  const content = (
    <>
      <div className="flex items-start justify-between gap-2 mb-2">
        <h4 className="text-sm font-semibold text-slate-900 leading-tight">
          {report.name}
        </h4>
        <div className="flex shrink-0 items-center gap-1">
          {emailed && (
            <span
              className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded bg-violet-50 text-violet-700"
              title="This report is emailed to you"
            >
              <MailOutlineIcon sx={{ fontSize: 12 }} />
              Emailed
            </span>
          )}
          <span
            className={`text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded ${badge.className}`}
          >
            {badge.label}
          </span>
        </div>
      </div>
      <p className="text-xs text-slate-600 leading-relaxed">
        {report.description}
      </p>
    </>
  );

  if (!isAvailable) {
    return (
      <div className={`${baseClasses} ${interactiveClasses}`} aria-disabled="true">
        {content}
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => onOpen(report.id)}
      className={`${baseClasses} ${interactiveClasses}`}
    >
      {content}
    </button>
  );
}

export default function PrebuiltReportsLanding({
  onOpenReport,
  storeNames,
  storeOptions,
  schedulingEnabled,
  permissionRole,
  userId,
}) {
  // Hide reports the user's role cannot access (internal-only rollouts), and
  // hide a report's tile when no accessible store can serve it — same
  // eligibility (manager+ membership, scheduling, requiredStoreFlag) the
  // display uses to block rendering, so tiles never dead-click back to this
  // page. Per-store, not home-store-level: one eligible alternate store
  // keeps the tile visible. Falls back to the home-store scheduling flag
  // while storeNames is still loading.
  const visibleReports = useMemo(() => {
    const stores = Array.isArray(storeNames) ? storeNames : [];
    return PREBUILT_REPORTS.filter((r) => {
      if (!reportVisibleForPermission(r, permissionRole)) return false;
      if (stores.length === 0) {
        return !(schedulingEnabled === false && reportRequiresScheduling(r));
      }
      return getEligibleStoresForReport(r, stores, storeOptions).length > 0;
    });
  }, [storeNames, storeOptions, permissionRole, schedulingEnabled]);

  // Which emailable reports reach this user ({report id: bool}), for the
  // "Emailed" badges. Starts from the last answer this session (so a return
  // visit shows badges at once) and refreshes in the background. Best-effort:
  // on failure the tiles keep whatever they had.
  const [emailedReports, setEmailedReports] = useState(
    () => getCachedEmailedReports(userId) || {}
  );
  useEffect(() => {
    let cancelled = false;
    const startedAtVersion = emailedReportsVersion();
    (async () => {
      try {
        const res = await getRightworkAPI(API_report_email_subscription, {});
        // A cancelled request (the page left, or another user signed in)
        // must not touch the cache either.
        if (cancelled) return;
        const reports = unwrapPayload(res)?.reports || {};
        // A switch change made while this was in flight wins over it.
        setEmailedReports(storeEmailedReports(userId, reports, startedAtVersion));
      } catch (e) {
        // Keep the cached badges rather than clearing them.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  const reportsByGroup = useMemo(() => {
    const map = {};
    for (const g of REPORT_GROUPS) map[g.id] = [];
    for (const r of visibleReports) {
      if (!map[r.group]) map[r.group] = [];
      map[r.group].push(r);
    }
    return map;
  }, [visibleReports]);

  const availableCount = visibleReports.filter(
    (r) => r.status === "available"
  ).length;
  const comingSoonCount = visibleReports.length - availableCount;

  const getGroupCounts = (reports) => {
    const available = reports.filter((r) => r.status === "available").length;
    return { available, total: reports.length };
  };

  return (
    <div className="bg-white min-h-screen">
      <div className="px-6 pt-6 pb-12 max-w-7xl">
        <div className="mb-6 flex items-start justify-between gap-6 sm:flex-col sm:gap-4">
          <div>
            <h3 className="text-xl font-semibold text-slate-900 mb-1">
              Report Library
            </h3>
            <p className="text-sm text-slate-600 max-w-3xl">
              Browse ready-to-use operations reports for schedules,
              labor, sales, and store performance.
            </p>
          </div>
          <div className="flex items-center gap-2 text-xs text-slate-600 sm:w-full sm:flex-wrap">
            <span className="rounded-full bg-emerald-50 text-emerald-700 px-3 py-1 font-semibold">
              {availableCount} available
            </span>
            <span className="rounded-full bg-slate-100 text-slate-700 px-3 py-1 font-semibold">
              {comingSoonCount} coming soon
            </span>
            <span className="rounded-full bg-slate-100 text-slate-700 px-3 py-1 font-semibold">
              {visibleReports.length} total
            </span>
          </div>
        </div>

        {REPORT_GROUPS.map((group) => {
          const reports = reportsByGroup[group.id] || [];
          if (reports.length === 0) return null;
          const counts = getGroupCounts(reports);
          return (
            <div key={group.id} className="mb-8">
              <div className="flex items-center justify-between gap-3 mb-3">
                <h4 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
                  {group.label}
                </h4>
                <span className="text-xs text-slate-500">
                  {counts.available} of {counts.total} available
                </span>
              </div>
              <div className="grid grid-cols-3 lg:grid-cols-2 sm:grid-cols-1 gap-3">
                {reports.map((r) => (
                  <ReportTile
                    key={r.id}
                    report={r}
                    onOpen={onOpenReport}
                    emailed={Boolean(emailedReports[r.id])}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
